// ===========================================
// Admin Schedule Dialog
// ===========================================
// Professional scheduling dialog for meeting & greet bookings
// Features date card selection, time slot grid, and optional custom time picker

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { addDays, format, startOfDay, isSameDay } from 'date-fns';
import { useMeetAndGreetAdminAvailability } from '../../hooks/useMeetAndGreet';
import { isTeacherAwayOnDay } from '../../utils/timeOff';
import { toLocalDateString } from '../../utils/dateUtils';

// ===========================================
// TYPES
// ===========================================

export interface AdminScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (isoDateTime: string) => void;
  currentDateTime: string | null;
  bookingId: string;
  isSaving: boolean;
}

interface TimeSlot {
  time: string;
  booked: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function AdminScheduleDialog({
  open,
  onClose,
  onSave,
  currentDateTime,
  bookingId,
  isSaving,
}: AdminScheduleDialogProps) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [customTimeValue, setCustomTimeValue] = useState('');

  // Fetch availability (excluding current booking)
  // Gated on `open`: the dialog stays mounted when closed (MUI default), so an
  // ungated query would refetch on every M&G mutation for a closed dialog.
  const { data: availability, isLoading, error } = useMeetAndGreetAdminAvailability(
    bookingId,
    open,
  );

  // Generate available dates
  const availableDates = useMemo(() => {
    if (!availability?.timeSlots || availability.timeSlots.length === 0) {
      return [];
    }

    const availableDaysOfWeek = new Set(
      availability.timeSlots
        .filter((slot) => slot.isActive)
        .map((slot) => slot.dayOfWeek)
    );

    const advanceBookingDays = availability?.settings?.advanceBookingDays || 14;
    const minimumNoticeHours = availability?.settings?.minimumNoticeHours || 24;

    const dates: Date[] = [];
    let currentDate = new Date();

    currentDate.setHours(currentDate.getHours() + minimumNoticeHours);
    currentDate = addDays(startOfDay(currentDate), 0);

    if (startOfDay(currentDate) <= startOfDay(new Date())) {
      currentDate = addDays(startOfDay(new Date()), 1);
    }

    const maxDate = addDays(new Date(), advanceBookingDays);

    while (currentDate <= maxDate && dates.length < 10) {
      const dayOfWeek = currentDate.getDay();
      if (availableDaysOfWeek.has(dayOfWeek)) {
        dates.push(startOfDay(currentDate));
      }
      currentDate = addDays(currentDate, 1);
    }

    return dates;
  }, [availability]);

  // Generate time slots for selected date
  const timeSlots = useMemo(() => {
    if (!selectedDate || !availability?.teachers || !availability?.timeSlots) return [];

    if (availability.teachers.length === 0) return [];

    const selectedDayOfWeek = selectedDate.getDay();
    const dayTimeSlots = availability.timeSlots.filter(
      (slot) => slot.dayOfWeek === selectedDayOfWeek && slot.isActive
    );

    if (dayTimeSlots.length === 0) return [];

    const sessionDuration = availability.settings?.sessionDuration || 15;
    const bufferTime = availability.settings?.bufferTime || 0;
    const bookedSlots = availability.bookedSlots || [];
    const teachers = availability.teachers;
    const unavailablePeriods = availability.unavailablePeriods || [];
    const selectedDayString = toLocalDateString(selectedDate);
    const slotMap = new Map<string, boolean>();

    for (const configuredSlot of dayTimeSlots) {
      const [startHours, startMinutes] = configuredSlot.startTime.split(':').map(Number);
      const [endHours, endMinutes] = configuredSlot.endTime.split(':').map(Number);

      const rangeStartMinutes = startHours * 60 + startMinutes;
      const rangeEndMinutes = endHours * 60 + endMinutes;

      let currentMinutes = rangeStartMinutes;
      while (currentMinutes + sessionDuration <= rangeEndMinutes) {
        const hours = Math.floor(currentMinutes / 60);
        const minutes = currentMinutes % 60;
        const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

        const anyTeacherFree = teachers.some((teacher) => {
          // A teacher on an APPROVED away period is not free that day at all.
          if (isTeacherAwayOnDay(unavailablePeriods, teacher.id, selectedDayString)) return false;

          const slotDateTime = new Date(selectedDate);
          slotDateTime.setHours(hours, minutes, 0, 0);

          const isBooked = bookedSlots.some((booked) => {
            if (!booked.dateTime || booked.teacherId !== teacher.id) return false;
            const bookedDate = new Date(booked.dateTime);
            return isSameDay(bookedDate, slotDateTime) &&
              bookedDate.getHours() === hours &&
              bookedDate.getMinutes() === minutes;
          });

          return !isBooked;
        });

        if (!slotMap.has(timeString)) {
          slotMap.set(timeString, anyTeacherFree);
        } else if (anyTeacherFree) {
          slotMap.set(timeString, true);
        }

        currentMinutes += sessionDuration + bufferTime;
      }
    }

    return Array.from(slotMap.entries())
      .map(([time, available]) => ({ time, booked: !available }))
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [selectedDate, availability]);

  // Handle dialog open - pre-populate if currentDateTime is set and availability is loaded
  if (open && currentDateTime && availableDates.length > 0 && !selectedDate) {
    const date = new Date(currentDateTime);
    const matchingDate = availableDates.find((d) => isSameDay(d, date));
    if (matchingDate && timeSlots.length > 0) {
      setSelectedDate(matchingDate);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const timeStr = `${hours}:${minutes}`;
      const slot = timeSlots.find((s) => s.time === timeStr);
      if (slot && !slot.booked) {
        setSelectedSlot(slot);
      } else if (!slot) {
        // Custom time if not in preset slots
        setUseCustomTime(true);
        setCustomTimeValue(date.toISOString().slice(0, 16));
      }
    }
  }

  // Reset state when dialog closes
  const handleClose = () => {
    setSelectedDate(null);
    setSelectedSlot(null);
    setUseCustomTime(false);
    setCustomTimeValue('');
    onClose();
  };

  // Handle save
  const handleSave = () => {
    let dateTime: string;

    if (useCustomTime && customTimeValue) {
      dateTime = new Date(customTimeValue).toISOString();
    } else if (selectedDate && selectedSlot) {
      const [hours, minutes] = selectedSlot.time.split(':').map(Number);
      const dateTimeObj = new Date(selectedDate);
      dateTimeObj.setHours(hours, minutes, 0, 0);
      dateTime = dateTimeObj.toISOString();
    } else {
      return;
    }

    onSave(dateTime);
    handleClose();
  };

  // Determine if save is enabled
  const canSave = useCustomTime && customTimeValue ? true : selectedDate && selectedSlot && !selectedSlot.booked ? true : false;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit Scheduled Date & Time</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load availability
          </Alert>
        )}

        {!isLoading && !error && (
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Available Dates */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Available Dates
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {availableDates.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No available dates
                  </Typography>
                ) : (
                  availableDates.map((date) => (
                    <Paper
                      key={date.toString()}
                      onClick={() => {
                        setSelectedDate(date);
                        setSelectedSlot(null);
                      }}
                      sx={{
                        p: 1.5,
                        minWidth: 80,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: isSameDay(date, selectedDate || new Date(0))
                          ? 'primary.main'
                          : 'divider',
                        bgcolor: isSameDay(date, selectedDate || new Date(0))
                          ? 'primary.light'
                          : 'background.paper',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                      }}
                    >
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {format(date, 'EEE')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {format(date, 'd')}
                      </Typography>
                    </Paper>
                  ))
                )}
              </Box>
            </Box>

            {/* Time Slots */}
            {selectedDate && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {format(selectedDate, 'EEEE, d MMMM yyyy')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                    gap: 1,
                  }}
                >
                  {timeSlots.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No available slots
                    </Typography>
                  ) : (
                    timeSlots.map((slot) => (
                      <Paper
                        key={slot.time}
                        onClick={() => {
                          if (!slot.booked) {
                            setSelectedSlot(slot);
                            setUseCustomTime(false);
                          }
                        }}
                        sx={{
                          p: 1,
                          textAlign: 'center',
                          cursor: slot.booked ? 'default' : 'pointer',
                          border: '2px solid',
                          borderColor: selectedSlot?.time === slot.time
                            ? 'primary.main'
                            : slot.booked ? 'divider' : 'divider',
                          bgcolor: selectedSlot?.time === slot.time
                            ? 'primary.light'
                            : slot.booked ? '#f5f5f5' : 'background.paper',
                          opacity: slot.booked ? 0.6 : 1,
                          transition: slot.booked ? 'none' : 'all 0.2s',
                          '&:hover': slot.booked ? {} : {
                            borderColor: 'primary.main',
                            boxShadow: 1,
                          },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {slot.time}
                        </Typography>
                        {slot.booked && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Booked
                          </Typography>
                        )}
                      </Paper>
                    ))
                  )}
                </Box>
              </Box>
            )}

            {/* Custom Time Option */}
            <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useCustomTime}
                    onChange={(e) => {
                      setUseCustomTime(e.target.checked);
                      if (!e.target.checked) {
                        setCustomTimeValue('');
                      }
                    }}
                  />
                }
                label="Use a different time"
              />
              {useCustomTime && (
                <TextField
                  type="datetime-local"
                  value={customTimeValue}
                  onChange={(e) => setCustomTimeValue(e.target.value)}
                  fullWidth
                  label="Date & Time"
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!canSave || isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
